/**
 * Finding cameras instead of typing them.
 *
 * The flow this replaces: an installer opens the router's DHCP table, writes down
 * an IP, then types two full RTSP URIs by hand — per camera, on a ladder. That is
 * the weakest part of the install story and every character of it is a chance to
 * transpose a digit into a camera that silently never records.
 *
 * Two stages, and the second one needs credentials. The device scans without
 * them, the operator picks a camera and supplies a credential once, and the
 * device reads the stream URIs off the camera itself.
 */

import { useState, type FormEvent } from 'react';

import { sealCameraCredential } from '@jeprotect/shared/camera-credential';

import {
  addCamera,
  ApiError,
  probeCamera,
  scanForCameras,
  type DiscoveredCamera,
  type ProbeResult,
} from './api.ts';
import { useDeviceKey } from './useDeviceKey.ts';

type Stage =
  | { name: 'idle' }
  | { name: 'scanning' }
  | { name: 'picked'; camera: DiscoveredCamera }
  | { name: 'probing'; camera: DiscoveredCamera }
  | { name: 'probed'; camera: DiscoveredCamera; result: ProbeResult };

/** A camera's own advertised name, which is unauthenticated text. Shown, never trusted. */
function describe(camera: DiscoveredCamera): string {
  const parts = [camera.name, camera.manufacturer, camera.model].filter(
    (part): part is string => typeof part === 'string' && part.trim() !== '',
  );
  return parts.length > 0 ? parts.join(' · ') : 'Unidentified camera';
}

export function ScanForCameras({
  deviceId,
  onAdded,
}: {
  deviceId: string;
  onAdded: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>({ name: 'idle' });
  const [found, setFound] = useState<DiscoveredCamera[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const key = useDeviceKey(deviceId, open);

  function reset(): void {
    setStage({ name: 'idle' });
    setFound(null);
    setError(null);
    setUsername('');
    setPassword('');
    setName('');
  }

  async function scan(): Promise<void> {
    setError(null);
    setStage({ name: 'scanning' });
    try {
      const result = await scanForCameras(deviceId);
      setFound(result.cameras);
      setStage({ name: 'idle' });
    } catch (err) {
      /*
       * "The device is offline" is a different problem from "the scan failed",
       * and both are different from "no cameras answered". The server
       * distinguishes them, so this does too rather than flattening them.
       */
      setError(err instanceof ApiError ? err.message : 'The scan could not be run.');
      setStage({ name: 'idle' });
    }
  }

  async function probe(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (stage.name !== 'picked') return;
    if (key.status !== 'ready') {
      setError('The device encryption key is not available, so it cannot be given credentials.');
      return;
    }
    if (username.trim() === '' || password === '') {
      setError('The camera’s username and password are both required.');
      return;
    }

    const camera = stage.camera;
    setStage({ name: 'probing', camera });
    setError(null);

    try {
      /*
       * Sealed exactly as a saved credential is. The cloud relays it and cannot
       * read it; only this device can open it (ADR 0007).
       */
      const result = await probeCamera(deviceId, camera.serviceUrl, {
        sealed: await sealCameraCredential(key.publicKey, {
          username: username.trim(),
          password,
        }),
        keyFingerprint: key.fingerprint,
      });

      // Prefill the name from what the camera calls itself, so the common case
      // is one click.
      setName((current) => current || camera.name || '');
      setStage({ name: 'probed', camera, result });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'The camera did not answer, or was rejected.',
      );
      setStage({ name: 'picked', camera });
    }
  }

  async function add(): Promise<void> {
    if (stage.name !== 'probed' || key.status !== 'ready') return;
    setBusy(true);
    setError(null);

    try {
      await addCamera({
        deviceId,
        name: name.trim() === '' ? undefined : name.trim(),
        onvifHost: stage.camera.address.split(':')[0] ?? stage.camera.address,
        subProfileUri: stage.result.subProfileUri,
        mainProfileUri: stage.result.mainProfileUri ?? undefined,
        /*
         * Sealed again for the row rather than reusing the probe's ciphertext.
         * The probe relayed a credential; it did not store one, and the sealed
         * value is single-purpose by construction — every seal is a fresh
         * ephemeral key.
         */
        credentials: {
          sealed: await sealCameraCredential(key.publicKey, {
            username: username.trim(),
            password,
          }),
          keyFingerprint: key.fingerprint,
        },
      });

      reset();
      setOpen(false);
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the camera.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn btn--sm" onClick={() => setOpen(true)}>
        Scan for cameras
      </button>
    );
  }

  return (
    <div className="scan">
      <div className="scan__head">
        <h4>Cameras on this device’s network</h4>
        <div className="form__actions" style={{ margin: 0 }}>
          <button
            type="button"
            className="btn btn--sm btn--primary"
            disabled={stage.name === 'scanning'}
            onClick={() => void scan()}
          >
            {stage.name === 'scanning' ? 'Scanning…' : found ? 'Scan again' : 'Scan'}
          </button>
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => {
              reset();
              setOpen(false);
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Said before the first scan, because several seconds of nothing reads as
          a broken button. */}
      {stage.name === 'scanning' ? (
        <p className="form__hint">
          Listening for cameras. This takes a few seconds — they answer on their own schedule.
        </p>
      ) : null}

      {key.status === 'absent' ? (
        <p className="notice notice--warn">
          This device has not claimed itself yet, so it cannot be given camera credentials.
        </p>
      ) : null}

      {found !== null && found.length === 0 && stage.name === 'idle' ? (
        <div className="empty">
          <p className="empty__title">No cameras answered</p>
          <p>
            They may be on a different subnet, or have discovery disabled — many do by default.
            Adding one by hand still works.
          </p>
        </div>
      ) : null}

      {found && found.length > 0 ? (
        <ul className="scan__list">
          {found.map((camera) => (
            <li key={camera.serviceUrl} className="scan__item">
              <div className="scan__ident">
                <span className="scan__name">{describe(camera)}</span>
                <span className="scan__addr">{camera.address}</span>
              </div>

              {camera.alreadyAdded ? (
                // Rather than hiding it: an operator scanning for a camera they
                // cannot find should see that it is already here.
                <span className="pill pill--muted">Already added</span>
              ) : (
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={key.status !== 'ready' || stage.name === 'probing'}
                  onClick={() => {
                    setError(null);
                    setStage({ name: 'picked', camera });
                  }}
                >
                  Use this one
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {stage.name === 'picked' || stage.name === 'probing' ? (
        <form className="form scan__panel" onSubmit={(e) => void probe(e)}>
          <p className="form__hint">
            Credentials for <strong>{stage.camera.address}</strong>. Encrypted in your browser to
            this device — JE Protect cannot read them.
          </p>

          <div className="field">
            <label htmlFor="scan-user">Camera username</label>
            <input
              id="scan-user"
              value={username}
              autoComplete="off"
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
            />
          </div>
          <div className="field">
            <label htmlFor="scan-pass">Camera password</label>
            <input
              id="scan-pass"
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="form__actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={stage.name === 'probing'}>
              {stage.name === 'probing' ? 'Asking the camera…' : 'Read stream settings'}
            </button>
            <button type="button" className="btn btn--sm" onClick={() => setStage({ name: 'idle' })}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {stage.name === 'probed' ? (
        <div className="form scan__panel">
          <p className="notice notice--ok">
            {stage.result.manufacturer ?? 'Camera'} {stage.result.model ?? ''} answered.{' '}
            {stage.result.profiles.length} profile
            {stage.result.profiles.length === 1 ? '' : 's'} found.
          </p>

          <div className="field">
            <label htmlFor="scan-name">Camera name</label>
            <input
              id="scan-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Front door"
            />
          </div>

          {/* Shown, not editable. These were read from the camera; an operator
              who needs to change them wants the manual form, and a typo here
              would undo the entire point of asking. */}
          <div className="field">
            <span className="field__label">Substream (recorded continuously)</span>
            <code className="scan__uri">{stage.result.subProfileUri}</code>
          </div>
          {stage.result.mainProfileUri ? (
            <div className="field">
              <span className="field__label">Mainstream (event only)</span>
              <code className="scan__uri">{stage.result.mainProfileUri}</code>
            </div>
          ) : null}

          <div className="form__actions">
            <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => void add()}>
              {busy ? 'Adding…' : 'Add this camera'}
            </button>
            <button type="button" className="btn btn--sm" onClick={() => setStage({ name: 'idle' })}>
              Back
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
